import {
  TItemsComponentFilterCardConfiguration,
  TItemsComponentItemScopeConfiguration,
  TItemsComponentItemViewConfiguration,
} from '../../pages/items/items-filter-card/items-filter-card.interface';
import { ELanguage } from '../../utilities/language.constants';
import { TDecimalSeparator, TThousandSeparator } from '../../utilities/number-format-utilities.service';
import { ELoadStatus } from '../state.interface';

export interface IUserState {
  getCurrentUserStatus: ELoadStatus;
  updateUserConfigurationsStatus: ELoadStatus;
  user: ICurrentUser | null;
}

export interface IUser {
  configuration: IUserConfiguration;
  defaultBoardId?: number | null;
  authorizedWorkspaceIds?: number[];
  email: string | null;
  id: number;
  isActive: boolean;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  name: string;
  pageConfiguration: IPageConfiguration | null;
  title: string;
}

export enum EAuthProvider {
  actionTracker = 'actionTracker',
  digitalFactory = 'digitalFactory',
}

export interface ISSOClient {
  loginString?: string;
  authProvider?: EAuthProvider;
}

export interface ICurrentUser extends IUser {
  /**
   * The environment's `AI_ENABLED` and the client's `client_info.is_ai_enabled` combined by the API; AI controls stay
   * hidden while it is false, and the API refuses every AI call.
   */
  aiEnabled: boolean;
  authorizations: IRequestUserAuthorizations;
  authProvider: EAuthProvider;
  moduleUrls: {
    oeeTrackerBackendUrl: string;
  };
}

export interface IRequestUserAuthorizations {
  adminBoardIds: number[] | -1;
  boardIds: number[] | -1;
  escalatedBoardIds: number[] | -1;
}

export type TAddUser = Omit<IUser, 'email' | 'id' | 'pageConfiguration' | 'isSuperAdmin'> & {
  email?: string;
  isSuperAdmin?: boolean;
};
export type TBulkEditUser = Partial<IUser> & { id: number };

export interface IEditUser {
  dto: TAddUser;
  id: number;
}

export interface IUserConfiguration {
  dateFormat: string;
  decimalSeparator: TDecimalSeparator;
  defaultBoard?: number | null;
  language: ELanguage;
  locale: string;
  thousandSeparator: TThousandSeparator;
  timezone: string;
}

type TComponentConfigurationValue =
  | string
  | string[]
  | number
  | number[]
  | null
  | IPageConfigurationValue<string, TComponentConfigurationValue>[];

export interface IPageConfigurationValue<Name extends string, ValueType extends TComponentConfigurationValue> {
  name: Name;
  value: ValueType;
}

export type TItemsComponentDatatableConfiguration = IPageConfigurationValue<
  'datatable',
  IPageConfigurationValue<string, number[]>[]
>;

export type TItemsComponentDatatableSortConfiguration = IPageConfigurationValue<
  'datatableSort',
  TBoardSortConfiguration[]
>;

export type TBoardSortConfiguration = IPageConfigurationValue<string, IPageConfigurationValue<string, string>[] | null>;

export type TItemsComponentPageConfiguration =
  | TItemsComponentDatatableConfiguration
  | TItemsComponentDatatableSortConfiguration
  | TItemsComponentFilterCardConfiguration
  | TItemsComponentItemScopeConfiguration
  | TItemsComponentItemViewConfiguration;

export interface IPageConfiguration {
  HomeComponent?: IPageConfigurationValue<'pinnedBoards', number[]>[];
  ItemsComponent?: TItemsComponentPageConfiguration[];
}
